import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api, ApiError } from '../../lib/api';
import { activeMembership, useSession } from '../../lib/store';
import { Card, Notice, PageHeading, Spinner } from '../../components/Shell';
import { CopyLink } from '../../components/CopyLink';
import { CustomDomainCard } from '../../components/CustomDomainCard';
import { BrandingCard } from '../../components/BrandingCard';
import { Stamp } from '../../components/Stamp';
import { SelectField, TextArea, TextField, Toggle } from '../../components/Field';
import { money } from '../../lib/format';
import { tenantSiteUrl } from '../../lib/config';
import type { Branding, MessagingSettings } from '../../lib/types';

/** A row in the staff directory: an active member, or an invite still pending. */
interface StaffRow {
  userId: string | null;
  name: string | null;
  email: string;
  role: 'owner' | 'staff';
  status: 'invited' | 'active' | 'revoked' | 'expired';
  invitedAt?: string;
}

const STAFF_STAMPS: Record<StaffRow['status'], { label: string; tone: 'ok' | 'pending' | 'flag' | 'quiet' }> = {
  active: { label: 'Active', tone: 'ok' },
  invited: { label: 'Invited', tone: 'pending' },
  expired: { label: 'Invite expired', tone: 'flag' },
  revoked: { label: 'Revoked', tone: 'quiet' },
};

interface TenantSettings {
  timezone: string;
  boardingCapacity: number;
  dayCareCapacity: number;
  holidayDates: string[];
  assessmentRequiredAfterMonths: number;
  requireSignedAgreement: boolean;
  requireCurrentVaccinations: boolean;
  payment: {
    acceptedMethods: string[];
    methodSurchargePct: Record<string, number>;
    depositThreshold: number;
    depositPct: number;
    balanceDueDaysBefore: number;
    lateRebookingFee: number;
    cancellationNoticeDays: number;
    manualPaymentInstructions?: string;
    stripeAccountId?: string;
    stripeChargesEnabled?: boolean;
  };
  googleCalendar: {
    connected: boolean;
    calendarId?: string;
    lastSyncAt?: string;
    lastSyncError?: string;
  };
  messaging: MessagingSettings;
}

interface FullTenant {
  tenantId: string;
  slug: string;
  businessName: string;
  branding: Branding;
  publicEmail: string;
  publicPhone?: string;
  serviceArea?: string;
  about?: string;
  /** Absent on a business signed up before plans existed, which means Solo. */
  plan?: 'solo' | 'professional' | 'team';
  settings: TenantSettings;
}

const PAYMENT_METHODS = [
  { value: 'card', label: 'Card' },
  { value: 'zelle', label: 'Zelle' },
  { value: 'venmo', label: 'Venmo' },
  { value: 'cash', label: 'Cash' },
  { value: 'bank_transfer', label: 'Bank transfer' },
  { value: 'check', label: 'Check' },
];

/**
 * Business settings: how the sitter takes money, how full they let themselves
 * get, and whether bookings mirror into the Google Calendar they already share
 * with clients.
 */
export function AdminSettings() {
  const session = useSession();
  const [searchParams] = useSearchParams();
  const tenantId = session.tenant?.tenantId;

  const [tenant, setTenant] = useState<FullTenant | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const googleResult = searchParams.get('google');
  const stripeResult = searchParams.get('stripe');

  const load = () => {
    if (!tenantId) return Promise.resolve();
    return api<{ tenant: FullTenant }>(`/tenants/${tenantId}`)
      .then((result) => setTenant(result.tenant))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Settings could not be loaded.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  // Coming back from Stripe onboarding, ask Stripe whether it actually finished.
  useEffect(() => {
    if (stripeResult !== 'return' || !tenantId) return;
    void api<{ chargesEnabled: boolean; requirementsDue: string[] }>(`/tenants/${tenantId}/stripe/status`)
      .then((status) => {
        setNotice(
          status.chargesEnabled
            ? 'Card payments are live.'
            : `Stripe still needs: ${status.requirementsDue.join(', ') || 'a few more details'}.`
        );
        return load();
      })
      .catch(() => setNotice('Could not check with Stripe. Try the button below.'));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stripeResult, tenantId]);

  if (loading) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Spinner label="Loading settings" />
      </div>
    );
  }

  if (!tenant || !tenantId) {
    return (
      <div className="mx-auto max-w-3xl px-5 py-16">
        <Notice tone="error">{error ?? 'Settings are not available.'}</Notice>
      </div>
    );
  }

  const settings = tenant.settings;

  const patchSettings = (patch: Partial<TenantSettings>) =>
    setTenant((prev) => (prev ? { ...prev, settings: { ...prev.settings, ...patch } } : prev));

  const patchPayment = (patch: Partial<TenantSettings['payment']>) =>
    setTenant((prev) =>
      prev
        ? { ...prev, settings: { ...prev.settings, payment: { ...prev.settings.payment, ...patch } } }
        : prev
    );

  const save = (event: React.FormEvent) => {
    event.preventDefault();
    setSaving(true);
    setSaved(false);
    setError(null);

    void api<{ tenant: FullTenant }>(`/tenants/${tenantId}`, {
      method: 'PATCH',
      body: {
        businessName: tenant.businessName,
        publicEmail: tenant.publicEmail,
        publicPhone: tenant.publicPhone,
        serviceArea: tenant.serviceArea,
        about: tenant.about,
        settings: {
          boardingCapacity: settings.boardingCapacity,
          dayCareCapacity: settings.dayCareCapacity,
          holidayDates: settings.holidayDates,
          assessmentRequiredAfterMonths: settings.assessmentRequiredAfterMonths,
          requireSignedAgreement: settings.requireSignedAgreement,
          requireCurrentVaccinations: settings.requireCurrentVaccinations,
          payment: {
            acceptedMethods: settings.payment.acceptedMethods,
            methodSurchargePct: settings.payment.methodSurchargePct,
            depositThreshold: settings.payment.depositThreshold,
            depositPct: settings.payment.depositPct,
            balanceDueDaysBefore: settings.payment.balanceDueDaysBefore,
            lateRebookingFee: settings.payment.lateRebookingFee,
            cancellationNoticeDays: settings.payment.cancellationNoticeDays,
            manualPaymentInstructions: settings.payment.manualPaymentInstructions,
          },
        },
      },
    })
      .then((result) => {
        setTenant(result.tenant);
        setSaved(true);
        window.scrollTo({ top: 0, behavior: 'smooth' });
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Settings did not save.'))
      .finally(() => setSaving(false));
  };

  const connectStripe = () => {
    void api<{ onboardingUrl: string }>(`/tenants/${tenantId}/stripe/connect`, { method: 'POST' })
      .then((result) => {
        window.location.href = result.onboardingUrl;
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Stripe could not be reached.'));
  };

  const connectGoogle = () => {
    void api<{ authUrl: string }>(`/tenants/${tenantId}/google/auth-url`)
      .then((result) => {
        window.location.href = result.authUrl;
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'Google could not be reached.'));
  };

  const disconnectGoogle = () => {
    void api(`/tenants/${tenantId}/google`, { method: 'DELETE' })
      .then(() => {
        setNotice('Google Calendar disconnected. Events already written are left alone.');
        return load();
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That did not work.'));
  };

  const syncNow = () => {
    void api<{ result: { created: number; updated: number; removed: number } }>(
      `/tenants/${tenantId}/google/sync`,
      { method: 'POST' }
    )
      .then((result) =>
        setNotice(
          `Calendar synced: ${result.result.created} added, ${result.result.updated} updated, ${result.result.removed} removed.`
        )
      )
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The sync did not finish.'));
  };

  const toggleMethod = (value: string, on: boolean) =>
    patchPayment({
      acceptedMethods: on
        ? [...new Set([...settings.payment.acceptedMethods, value])]
        : settings.payment.acceptedMethods.filter((m) => m !== value),
    });

  // Solo is paid for out of card payments, so it is card only. The toggles stay
  // visible rather than disappearing: what you would get by upgrading is the
  // point, and hiding it would just make the screen look broken.
  const takesMoneyOffPlatform = tenant.plan === 'professional' || tenant.plan === 'team';

  return (
    <div className="mx-auto max-w-3xl px-5 pb-24 pt-12">
      <PageHeading eyebrow={tenant.businessName} title="Settings" />

      <Card tab="Your address" className="mb-6 p-5">
        <CopyLink
          url={tenantSiteUrl(tenant.slug)}
          label="Where clients find you. The slug is fixed once you are live, because this link is out in the world."
        />
      </Card>

      <CustomDomainCard tenantId={tenant.tenantId} />

      <BrandingCard tenantId={tenant.tenantId} branding={tenant.branding} />

      <StaffCard tenantId={tenant.tenantId} canManage={activeMembership(session)?.role === 'owner'} />

      {activeMembership(session)?.role === 'owner' && <ExportCard tenantId={tenant.tenantId} />}

      {saved && (
        <div className="mb-8">
          <Notice tone="ok">Settings saved.</Notice>
        </div>
      )}
      {notice && (
        <div className="mb-8">
          <Notice tone="info">{notice}</Notice>
        </div>
      )}
      {googleResult === 'connected' && (
        <div className="mb-8">
          <Notice tone="ok">Google Calendar connected. Confirmed bookings appear on it within 15 minutes.</Notice>
        </div>
      )}
      {googleResult === 'failed' && (
        <div className="mb-8">
          <Notice tone="error">Google did not complete the connection. Try again below.</Notice>
        </div>
      )}
      {error && (
        <div className="mb-8">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {/* --- Integrations sit outside the form: they navigate away. --------- */}
      <Card tab="Card payments" className="mb-6 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display text-lg font-bold">Stripe</p>
            <p className="text-sm text-[color:var(--color-ink-soft)]">
              Money goes straight to your own Stripe account. We never hold it.
            </p>
          </div>
          {settings.payment.stripeChargesEnabled ? (
            <Stamp tone="ok" flat>
              Taking cards
            </Stamp>
          ) : settings.payment.stripeAccountId ? (
            <Stamp tone="pending" flat>
              Setup unfinished
            </Stamp>
          ) : (
            <Stamp tone="quiet" flat>
              Not connected
            </Stamp>
          )}
        </div>
        <button type="button" className="btn btn-secondary mt-4" onClick={connectStripe}>
          {settings.payment.stripeAccountId ? 'Finish Stripe setup' : 'Connect Stripe'}
        </button>
      </Card>

      <Card tab="Calendar" className="mb-8 p-5">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <p className="font-display text-lg font-bold">Google Calendar</p>
            <p className="text-sm text-[color:var(--color-ink-soft)]">
              Confirmed bookings are written to the calendar you already share with clients.
            </p>
          </div>
          {settings.googleCalendar.connected ? (
            <Stamp tone="ok" flat>
              Connected
            </Stamp>
          ) : (
            <Stamp tone="quiet" flat>
              Not connected
            </Stamp>
          )}
        </div>

        {settings.googleCalendar.lastSyncError && (
          <p className="mt-3 text-sm text-[color:var(--color-flag)]">
            Last sync failed: {settings.googleCalendar.lastSyncError}
          </p>
        )}
        {settings.googleCalendar.lastSyncAt && (
          <p className="mt-2 font-mono text-xs text-[color:var(--color-ink-faint)]">
            Last synced {new Date(settings.googleCalendar.lastSyncAt).toLocaleString()}
          </p>
        )}

        <div className="mt-4 flex flex-wrap gap-2">
          {settings.googleCalendar.connected ? (
            <>
              <button type="button" className="btn btn-secondary" onClick={syncNow}>
                Sync now
              </button>
              <button type="button" className="btn btn-quiet" onClick={disconnectGoogle}>
                Disconnect
              </button>
            </>
          ) : (
            <button type="button" className="btn btn-secondary" onClick={connectGoogle}>
              Connect Google Calendar
            </button>
          )}
        </div>
      </Card>

      {tenantId && (
        <MessagingCard
          tenantId={tenantId}
          messaging={settings.messaging}
          onChange={(messaging) => patchSettings({ messaging })}
        />
      )}

      <form onSubmit={save} className="space-y-8">
        <Card tab="Your business" className="p-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Business name"
              value={tenant.businessName}
              onChange={(value) => setTenant({ ...tenant, businessName: value })}
              required
            />
            <TextField
              label="Contact email"
              type="email"
              value={tenant.publicEmail}
              onChange={(value) => setTenant({ ...tenant, publicEmail: value })}
              required
            />
            <TextField
              label="Phone"
              type="tel"
              value={tenant.publicPhone ?? ''}
              onChange={(value) => setTenant({ ...tenant, publicPhone: value })}
            />
            <TextField
              label="Area you cover"
              value={tenant.serviceArea ?? ''}
              onChange={(value) => setTenant({ ...tenant, serviceArea: value })}
            />
          </div>
          <div className="mt-5">
            <TextArea
              label="About your care"
              value={tenant.about ?? ''}
              onChange={(value) => setTenant({ ...tenant, about: value })}
              rows={4}
            />
          </div>
        </Card>

        <Card tab="How full you go" tint="blue" className="p-5">
          <div className="grid gap-5 sm:grid-cols-2">
            <TextField
              label="Boarding guests per night"
              type="number"
              value={String(settings.boardingCapacity)}
              onChange={(value) => patchSettings({ boardingCapacity: Number(value) || 0 })}
              hint="A night at capacity stops taking bookings on its own."
            />
            <TextField
              label="Day care places per day"
              type="number"
              value={String(settings.dayCareCapacity)}
              onChange={(value) => patchSettings({ dayCareCapacity: Number(value) || 0 })}
            />
            <TextField
              label="Assessment expires after (months)"
              type="number"
              value={String(settings.assessmentRequiredAfterMonths)}
              onChange={(value) => patchSettings({ assessmentRequiredAfterMonths: Number(value) || 0 })}
              hint="Pets who have not visited in this long need a fresh assessment."
            />
          </div>

          <div className="mt-5 space-y-1">
            <Toggle
              label="Require a signed agreement before confirming"
              checked={settings.requireSignedAgreement}
              onChange={(value) => patchSettings({ requireSignedAgreement: value })}
            />
            <Toggle
              label="Require current vaccination records"
              checked={settings.requireCurrentVaccinations}
              onChange={(value) => patchSettings({ requireCurrentVaccinations: value })}
            />
          </div>

          <div className="mt-5">
            <TextField
              label="Holiday dates"
              value={settings.holidayDates.join(', ')}
              onChange={(value) =>
                patchSettings({ holidayDates: value.split(',').map((d) => d.trim()).filter(Boolean) })
              }
              hint="MM-DD repeats every year. YYYY-MM-DD is that year only."
            />
          </div>
        </Card>

        <Card tab="Money" tint="yellow" className="p-5">
          <fieldset>
            <legend className="field-label">What you accept</legend>
            <div className="grid gap-1 sm:grid-cols-2">
              {PAYMENT_METHODS.map((method) => {
                const locked = method.value !== 'card' && !takesMoneyOffPlatform;
                return (
                  <Toggle
                    key={method.value}
                    label={method.label}
                    checked={!locked && settings.payment.acceptedMethods.includes(method.value)}
                    disabled={locked}
                    hint={locked ? 'Part of Professional' : undefined}
                    onChange={(on) => toggleMethod(method.value, on)}
                  />
                );
              })}
            </div>
            {!takesMoneyOffPlatform && (
              <p className="mt-3 text-xs text-[color:var(--color-ink-soft)]">
                On Solo your clients pay by card and we keep 5% of it. Move to Professional and
                that 5% goes away, and you can take Zelle, Venmo or cash as well.
              </p>
            )}
          </fieldset>

          <div className="mt-6 grid gap-5 sm:grid-cols-2">
            <TextField
              label="Deposit needed above"
              type="number"
              value={(settings.payment.depositThreshold / 100).toFixed(2)}
              onChange={(value) => patchPayment({ depositThreshold: Math.round(Number(value) * 100) || 0 })}
              hint={`Currently ${money(settings.payment.depositThreshold)}.`}
            />
            <TextField
              label="Deposit percentage"
              type="number"
              value={String(settings.payment.depositPct)}
              onChange={(value) => patchPayment({ depositPct: Number(value) || 0 })}
            />
            <TextField
              label="Balance due, days before"
              type="number"
              value={String(settings.payment.balanceDueDaysBefore)}
              onChange={(value) => patchPayment({ balanceDueDaysBefore: Number(value) || 0 })}
            />
            <TextField
              label="Late re-booking fee, per pet"
              type="number"
              value={(settings.payment.lateRebookingFee / 100).toFixed(2)}
              onChange={(value) => patchPayment({ lateRebookingFee: Math.round(Number(value) * 100) || 0 })}
            />
            <TextField
              label="Free cancellation notice, days"
              type="number"
              value={String(settings.payment.cancellationNoticeDays)}
              onChange={(value) => patchPayment({ cancellationNoticeDays: Number(value) || 0 })}
            />
            <SelectField
              label="Venmo processing surcharge"
              value={String(settings.payment.methodSurchargePct.venmo ?? 0)}
              onChange={(value) =>
                patchPayment({
                  methodSurchargePct: { ...settings.payment.methodSurchargePct, venmo: Number(value) },
                })
              }
              options={[0, 1, 2, 3, 4, 5].map((pct) => ({ value: String(pct), label: `${pct}%` }))}
            />
          </div>

          <div className="mt-5">
            <TextArea
              label="Instructions for paying you directly"
              value={settings.payment.manualPaymentInstructions ?? ''}
              onChange={(value) => patchPayment({ manualPaymentInstructions: value })}
              rows={3}
              placeholder="Zelle to karen@example.com, or Venmo @karen-rush. Put your pet's name in the memo."
              hint="Shown to a client who chooses to pay outside the card checkout."
            />
          </div>
        </Card>

        <button type="submit" className="btn btn-primary" disabled={saving}>
          {saving ? 'Saving' : 'Save settings'}
        </button>
      </form>
    </div>
  );
}

/**
 * Who can sign in and work on this business. Anyone staff or above can see
 * the list; only an owner can invite or revoke, so those controls are hidden
 * rather than shown disabled for a plain staff viewer.
 */
function StaffCard({ tenantId, canManage }: { tenantId: string; canManage: boolean }) {
  const [staff, setStaff] = useState<StaffRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [inviteRole, setInviteRole] = useState<'owner' | 'staff'>('staff');
  const [inviting, setInviting] = useState(false);
  const [revokingUserId, setRevokingUserId] = useState<string | null>(null);

  const load = () =>
    api<{ staff: StaffRow[] }>(`/tenants/${tenantId}/staff`)
      .then((result) => setStaff(result.staff))
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The staff list could not be loaded.'))
      .finally(() => setLoading(false));

  useEffect(() => {
    void load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tenantId]);

  const invite = (event: React.FormEvent) => {
    event.preventDefault();
    setInviting(true);
    setError(null);
    setNotice(null);

    void api<{ sent: boolean; reused: boolean }>(`/tenants/${tenantId}/staff/invite`, {
      method: 'POST',
      body: { email: inviteEmail, role: inviteRole },
    })
      .then((result) => {
        setNotice(
          result.sent
            ? result.reused
              ? 'Invite re-sent.'
              : 'Invite sent.'
            : 'Invite created, but the email did not send. Check the address.'
        );
        setInviteEmail('');
        return load();
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'The invite could not be sent.'))
      .finally(() => setInviting(false));
  };

  const revoke = (userId: string) => {
    if (!window.confirm("Revoke this person's access?")) return;
    setRevokingUserId(userId);
    setError(null);

    void api(`/tenants/${tenantId}/staff/${userId}`, { method: 'DELETE' })
      .then(() => load())
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That could not be revoked.'))
      .finally(() => setRevokingUserId(null));
  };

  return (
    <Card tab="Staff" className="mb-8 p-5">
      <p className="text-sm text-[color:var(--color-ink-soft)]">
        Who can sign in and work on this business's bookings.
      </p>

      {notice && (
        <div className="mt-4">
          <Notice tone="ok">{notice}</Notice>
        </div>
      )}
      {error && (
        <div className="mt-4">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      {loading ? (
        <div className="mt-4">
          <Spinner label="Loading staff" />
        </div>
      ) : staff.length === 0 ? (
        <p className="mt-4 text-sm text-[color:var(--color-ink-soft)]">No staff yet.</p>
      ) : (
        <ul className="mt-4 divide-y divide-[color:var(--color-rule)]">
          {staff.map((member) => (
            <li
              key={member.userId ?? member.email}
              className="flex flex-wrap items-center justify-between gap-3 py-3"
            >
              <div className="min-w-0">
                <p className="truncate font-display font-bold">{member.name ?? member.email}</p>
                {member.name && (
                  <p className="truncate typed text-sm text-[color:var(--color-ink-soft)]">{member.email}</p>
                )}
              </div>
              <div className="flex flex-wrap items-center gap-2">
                <span className="font-mono text-xs uppercase tracking-wider text-[color:var(--color-ink-faint)]">
                  {member.role}
                </span>
                <Stamp tone={STAFF_STAMPS[member.status].tone} flat>
                  {STAFF_STAMPS[member.status].label}
                </Stamp>
                {canManage && member.userId && member.status !== 'revoked' && (
                  <button
                    type="button"
                    className="btn btn-quiet"
                    disabled={revokingUserId === member.userId}
                    onClick={() => revoke(member.userId!)}
                  >
                    {revokingUserId === member.userId ? 'Revoking' : 'Revoke'}
                  </button>
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {canManage && (
        <form
          onSubmit={invite}
          className="mt-6 flex flex-wrap items-end gap-3 border-t border-[color:var(--color-rule)] pt-5"
        >
          <div className="min-w-[220px] flex-1">
            <TextField
              label="Invite by email"
              type="email"
              value={inviteEmail}
              onChange={setInviteEmail}
              placeholder="jamie@example.com"
              required
            />
          </div>
          <div className="w-40">
            <SelectField
              label="Role"
              value={inviteRole}
              onChange={(value) => setInviteRole(value as 'owner' | 'staff')}
              options={[
                { value: 'staff', label: 'Staff' },
                { value: 'owner', label: 'Owner' },
              ]}
            />
          </div>
          <button type="submit" className="btn btn-secondary" disabled={inviting || !inviteEmail}>
            {inviting ? 'Sending' : 'Send invite'}
          </button>
        </form>
      )}
    </Card>
  );
}

/**
 * "Your records are yours, and you can export what you keep here." Owner
 * only: the file this produces includes every client's personal details
 * (contact info, emergency contacts, home access notes) alongside the
 * business's own data, so handing it out is not a call staff get to make.
 *
 * The API hands back a presigned S3 URL rather than the file itself, and
 * that URL is cross-origin to this app. A plain `<a download>` is not
 * reliable for a cross-origin URL in every browser, so this fetches the
 * object itself, turns the response into a Blob, and saves it from an
 * object URL — which also lets the saved file get a real name instead of
 * whatever a browser tab would call a JSON response it navigated to
 * directly.
 */
function ExportCard({ tenantId }: { tenantId: string }) {
  const [exporting, setExporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runExport = () => {
    setExporting(true);
    setError(null);

    void api<{ downloadUrl: string; expiresInSeconds: number }>(`/tenants/${tenantId}/export`, {
      method: 'POST',
    })
      .then(async (result) => {
        const response = await fetch(result.downloadUrl);
        if (!response.ok) throw new Error('The export file could not be downloaded.');
        const blob = await response.blob();

        const objectUrl = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = objectUrl;
        link.download = `petservices-export-${tenantId}-${new Date().toISOString().slice(0, 10)}.json`;
        document.body.appendChild(link);
        link.click();
        link.remove();
        URL.revokeObjectURL(objectUrl);
      })
      .catch((err) =>
        setError(err instanceof ApiError ? err.message : 'The export could not be downloaded. Try again.')
      )
      .finally(() => setExporting(false));
  };

  return (
    <Card tab="Export my records" className="mb-8 p-5">
      <p className="text-sm text-[color:var(--color-ink-soft)]">
        A complete copy of everything this business holds: your business profile and
        settings, your service catalog, every client and pet on file, pet documents,
        bookings, signed agreements, and visit reports. It does not include your sign-in
        credentials, payment processing keys, or calendar connection secrets — those stay
        with us and are never something you need to keep a copy of.
      </p>
      <p className="mt-2 text-sm text-[color:var(--color-ink-soft)]">
        The download link only works for 5 minutes, so keep this tab open until the file
        saves.
      </p>

      {error && (
        <div className="mt-4">
          <Notice tone="error">{error}</Notice>
        </div>
      )}

      <button type="button" className="btn btn-secondary mt-4" onClick={runExport} disabled={exporting}>
        {exporting ? 'Preparing your export' : 'Export my records'}
      </button>
    </Card>
  );
}

const STATUS_COPY: Record<MessagingSettings['status'], { tone: 'ok' | 'quiet' | 'flag'; label: string }> = {
  not_started: { tone: 'quiet', label: 'Not set up' },
  pending_registration: { tone: 'quiet', label: 'Awaiting carrier approval' },
  ready: { tone: 'ok', label: 'Sending' },
  suspended: { tone: 'flag', label: 'Suspended' },
};

/**
 * Texting: get a number, then write what goes out before a stay.
 *
 * The two-step status is the whole reason this screen is not just a toggle. A
 * number is bought in seconds and then sits unusable for days while carriers
 * approve the A2P registration behind it, and a sitter who is not told that
 * will send a photo, see no error, and conclude the product is broken. So the
 * waiting state is named on the card rather than hidden behind a spinner.
 */
function MessagingCard({
  tenantId,
  messaging,
  onChange,
}: {
  tenantId: string;
  messaging: MessagingSettings;
  onChange: (messaging: MessagingSettings) => void;
}) {
  const [areaCode, setAreaCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const status = STATUS_COPY[messaging.status];
  const hasNumber = Boolean(messaging.phoneNumber);

  const post = (body: Record<string, unknown>, onDone?: (m: MessagingSettings) => void) => {
    setBusy(true);
    setError(null);
    setNotice(null);
    void api<{ messaging: MessagingSettings; problem?: string }>(`/tenants/${tenantId}/messaging`, {
      method: 'POST',
      body,
    })
      .then((result) => {
        onChange(result.messaging);
        if (result.problem) setError(result.problem);
        else onDone?.(result.messaging);
      })
      .catch((err) => setError(err instanceof ApiError ? err.message : 'That could not be saved.'))
      .finally(() => setBusy(false));
  };

  return (
    <Card tab="Texting" className="mb-8 p-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="min-w-0">
          <p className="font-display text-lg font-bold">Text messages</p>
          <p className="text-sm text-[color:var(--color-ink-soft)]">
            Photos from a visit, reminders, and a note before a stay. Clients choose whether to
            receive them, and can stop them by replying STOP.
          </p>
        </div>
        <Stamp tone={status.tone} flat>
          {status.label}
        </Stamp>
      </div>

      {error && (
        <div className="mt-4">
          <Notice tone="error">{error}</Notice>
        </div>
      )}
      {notice && (
        <div className="mt-4">
          <Notice tone="ok">{notice}</Notice>
        </div>
      )}

      {!hasNumber ? (
        <div className="mt-5">
          <p className="mb-4 text-sm text-[color:var(--color-ink-soft)]">
            We will get you a number of your own. Clients see it, not us, and a local one gets
            answered where an out-of-state one gets ignored.
          </p>
          <div className="flex flex-wrap items-end gap-3">
            <div className="w-32">
              <TextField
                label="Area code"
                value={areaCode}
                onChange={setAreaCode}
                placeholder="704"
                hint="Optional"
              />
            </div>
            <button
              type="button"
              className="btn btn-primary"
              disabled={busy}
              onClick={() =>
                post({ provision: true, areaCode: areaCode || undefined, enabled: true }, () =>
                  setNotice('Your number is reserved. Carriers usually approve it within a few days.')
                )
              }
            >
              {busy ? 'Setting up' : 'Get a number'}
            </button>
          </div>
        </div>
      ) : (
        <>
          <div className="mt-5 border-t border-[color:var(--color-rule)] pt-5">
            <p className="typed text-sm">
              Your number: <span className="font-bold">{messaging.phoneNumber}</span>
            </p>
            {messaging.statusDetail && messaging.status !== 'ready' && (
              <p className="mt-2 text-sm text-[color:var(--color-ink-soft)]">{messaging.statusDetail}</p>
            )}
          </div>

          <div className="mt-5 space-y-5">
            <Toggle
              label="Send text messages"
              checked={messaging.enabled}
              onChange={(enabled) => post({ enabled })}
              hint="Turning this off stops every text at once. Your number is kept."
            />

            <TextField
              label="How you sign your texts"
              value={messaging.senderLabel ?? ''}
              onChange={(senderLabel) => onChange({ ...messaging, senderLabel })}
              hint="Shown at the start of each message, because nobody has your number saved yet."
            />

            <div className="border-t border-[color:var(--color-rule)] pt-5">
              <Toggle
                label="Text instructions before a stay"
                checked={messaging.preArrival.enabled}
                onChange={(enabled) =>
                  onChange({ ...messaging, preArrival: { ...messaging.preArrival, enabled } })
                }
                hint="The things only you know: which door, what to bring, where the key goes."
              />

              {messaging.preArrival.enabled && (
                <div className="mt-5 space-y-5">
                  <div className="w-40">
                    <TextField
                      label="Days before"
                      type="number"
                      min={1}
                      max={14}
                      value={String(messaging.preArrival.daysBefore)}
                      onChange={(value) =>
                        onChange({
                          ...messaging,
                          preArrival: { ...messaging.preArrival, daysBefore: Number(value) || 1 },
                        })
                      }
                    />
                  </div>
                  <TextArea
                    label="What it says"
                    value={messaging.preArrival.body}
                    onChange={(body) =>
                      onChange({ ...messaging, preArrival: { ...messaging.preArrival, body } })
                    }
                    rows={5}
                    hint="You can use {clientName}, {petNames}, {startDate} and {businessName}. Anything else is sent exactly as typed."
                  />
                </div>
              )}
            </div>

            <button
              type="button"
              className="btn btn-secondary"
              disabled={busy}
              onClick={() =>
                post(
                  {
                    senderLabel: messaging.senderLabel,
                    preArrivalEnabled: messaging.preArrival.enabled,
                    preArrivalDaysBefore: messaging.preArrival.daysBefore,
                    preArrivalBody: messaging.preArrival.body,
                  },
                  () => setNotice('Your messaging settings are saved.')
                )
              }
            >
              {busy ? 'Saving' : 'Save messaging settings'}
            </button>
          </div>
        </>
      )}
    </Card>
  );
}
